import React, { useState } from 'react';
import { getCurrentUser } from '@aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { post } from 'aws-amplify/api';
import { updateSessionAnalysis } from '../graphql/mutations';

const client = generateClient();

/**
 * Props:
 * - sessionId (required)
 * - onAdded?: (newAddendum) => void
 * - orgUuid?: string                 // falls back to sessionStorage 'pcc_orgUuid'
 * - token?: string                   // falls back to sessionStorage 'pcc_accessToken'
 * - pccNoteId?: string               // preferred id if provided
 * - facilityId?: string | number     // falls back to sessionStorage 'pcc_facId'
 * - hasPCCSession?: boolean          // derived if missing
 * - providerName?: string            // falls back to current user
 */
function AddAddendum({
  sessionId,
  onAdded,
  orgUuid: orgUuidProp,
  token: tokenProp,
  pccNoteId: pccNoteIdProp,
  facilityId: facilityIdProp,
  hasPCCSession: hasPCCSessionProp,
  providerName: providerNameProp,
}) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);

  // --- Session-scoped storage key (matches SessionResultView.js) ---
  const PCC_ID_KEY = `pcc_progressNoteId:${sessionId}`;

  // ---- Resolve PCC context (props → sessionStorage fallbacks) ----
  const orgUuid = orgUuidProp || sessionStorage.getItem('pcc_orgUuid') || null;
  const token = tokenProp || sessionStorage.getItem('pcc_accessToken') || null;

  // Prefer prop; then session-scoped key; then legacy global key (for very old pushes)
  const pccNoteId =
    pccNoteIdProp ||
    sessionStorage.getItem(PCC_ID_KEY) ||
    sessionStorage.getItem('pcc_progressNoteId') ||
    null;

  const facilityId =
    facilityIdProp != null ? facilityIdProp : (sessionStorage.getItem('pcc_facId') || '');

  const hasPCCSession = typeof hasPCCSessionProp === 'boolean'
    ? hasPCCSessionProp
    : !!(orgUuid && token);

  // --- Local save only (keeps your DB in sync and UI responsive) ---
  const handleAddLocal = async () => {
    const clean = text.trim();
    if (!clean) return;

    setSaving(true);
    try {
      const { username, signInDetails } = await getCurrentUser();
      const addedBy = signInDetails?.loginId || username || 'User';
      const addedAt = new Date().toISOString();

      const newAddendum = { text: clean, addedBy, addedAt };

      await client.graphql({
        query: updateSessionAnalysis,
        variables: {
          input: {
            id: sessionId,
            // NOTE: default AppSync update replaces the list; your backend should
            // merge/append server-side. If not, we can customize resolver later.
            addendums: [newAddendum],
          },
        },
      });

      onAdded?.(newAddendum);
      setText('');
      return newAddendum;
    } catch (err) {
      console.error('Failed to add addendum:', err);
      alert('Could not add addendum ❌');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // --- Push addendum to PCC (requires existing PCC note) ---
  const handlePushAddendumToPCC = async () => {
    const clean = text.trim();
    if (!clean) return alert('Enter addendum text first.');
    if (!hasPCCSession) return alert('⚠️ Not signed in with PointClickCare.');
    if (!pccNoteId) return alert('No PointClickCare note to add to. Push the note first.');
    if (!facilityId) return alert('Select a facility (same one used to push).');

    setPushing(true);
    try {
      // 1) Save locally first so UI/db reflect the addendum immediately
      await handleAddLocal();

      // 2) Send to PCC
      const apiCreatedDate = new Date().toISOString(); // UTC, ISO 8601 as required by PCC

      const resp = await post({
        apiName: 'pccapi',
        path: `/pushToPCC?sessionId=${encodeURIComponent(sessionId)}`,
        options: {
          body: {
            action: 'addendum',
            orgUuid,
            pccAccessToken: token,
            facilityId: Number(facilityId),
            pccNoteId,
            addendumText: clean,
            apiCreatedDate,
            providerName: providerNameProp ?? 'Provider',
          },
          headers: {
            'X-PCC-OrgUuid': orgUuid,
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      }).response;

      // PCC addendum success is 204 No Content
      if (resp?.statusCode !== 204) {
        const raw = await resp.body.text();
        console.error('PCC addendum failed:', resp?.statusCode, raw);
        return alert(`❌ Failed to push addendum to PointClickCare.\n${raw}`);
      }

      alert('✅ Addendum pushed to PointClickCare.');
      // text was already cleared by handleAddLocal()
    } catch (err) {
      console.error('Error pushing addendum to PointClickCare:', err);
      alert('❌ Failed to push addendum to PointClickCare.');
    } finally {
      setPushing(false);
    }
  };

  return (
    <div className="addendum-box">
      <textarea
        className="field-input"
        rows={3}
        placeholder="Enter addendum..."
        value={text}
        onChange={(e) => setText(e.target.value)}
      />

      <div className="mt-2" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button
          onClick={handleAddLocal}
          disabled={saving || !text.trim()}
          className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
        >
          {saving ? 'Adding…' : 'Add Addendum (Local)'}
        </button>

        <button
          onClick={handlePushAddendumToPCC}
          disabled={
            pushing ||
            !text.trim() ||
            !hasPCCSession ||
            !pccNoteId ||
            !facilityId
          }
          className="bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700"
          title={
            !hasPCCSession
              ? 'Sign in with PointClickCare first'
              : !pccNoteId
              ? 'Push the note to PointClickCare first'
              : !facilityId
              ? 'Select a facility'
              : undefined
          }
        >
          {pushing ? 'Pushing…' : 'Push Addendum to PointClickCare'}
        </button>
      </div>

      <div className="text-muted" style={{ marginTop: '0.25rem', fontSize: '0.85rem' }}>
        {hasPCCSession ? '✅ Connected to PointClickCare' : '⚠️ Not connected to PointClickCare'}
        {pccNoteId ? ` • Note #${pccNoteId}` : ' • No PointClickCare note yet'}
        {facilityId ? ` • Facility ${facilityId}` : ' • Facility not selected'}
      </div>
    </div>
  );
}

export default AddAddendum;
